import { lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/sections/Hero";
import SaintVideoSection from "@/components/SaintVideoSection";
import { YouTubeFacade } from "@/components/YouTubeFacade";
import BrandSectionDivider from "@/components/sections/BrandSectionDivider";
import SEOHead from "@/components/SEOHead";
import { CLINIC_REF, SITE_ID, SITE_URL } from "@/components/schemaConstants";

// Below-fold sections are lazy-loaded so their JS (Radix accordion, embla
// carousel, react-hook-form, large content blocks) ships in separate chunks
// that load after hydration — not in the initial page chunk. TanStack Start's
// streaming SSR still renders their content for crawlers/AI; the client only
// pays the parse/execute cost after first paint.
const TmsTherapy = lazy(() => import("@/components/sections/TmsTherapy"));
const SaintStats = lazy(() => import("@/components/sections/SaintStats"));
const HowItWorks = lazy(() => import("@/components/sections/HowItWorks"));
const TwoWaysToGetStarted = lazy(
  () => import("@/components/sections/TwoWaysToGetStarted"),
);
const WhyUs = lazy(() => import("@/components/sections/WhyUs"));
const Testimonials = lazy(() => import("@/components/sections/Testimonials"));
const BrighterTomorrowBanner = lazy(
  () => import("@/components/sections/BrighterTomorrowBanner"),
);
const SaintComparisonCallout = lazy(
  () => import("@/components/sections/SaintComparisonCallout"),
);
const Faq = lazy(() => import("@/components/sections/Faq"));
const NewsAndHappenings = lazy(
  () => import("@/components/sections/NewsAndHappenings"),
);
const Consultation = lazy(() => import("@/components/sections/Consultation"));
const Footer = lazy(() => import("@/components/sections/Footer"));
const RelatedPages = lazy(() =>
  import("@/components/RelatedPages").then((m) => ({
    default: m.RelatedPages,
  })),
);

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="SAINT® TMS in Coral Springs, FL | Verified SAINT Provider"
        description="SAINT® is an FDA-cleared, fMRI-guided 5-day TMS protocol for treatment-resistant depression. Verified SAINT Provider in Coral Springs, FL. Free consultation."
        canonical="/"
        keywords="SAINT TMS Coral Springs, Stanford accelerated TMS, 5-day TMS depression treatment, fMRI-guided TMS, treatment-resistant depression South Florida, Verified SAINT Provider, FDA-cleared TMS"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "@id": `${SITE_URL}/#webpage`,
            name: "SAINT® TMS in Coral Springs, FL | Verified SAINT Provider",
            description:
              "SAINT® is an FDA-cleared, fMRI-guided 5-day TMS protocol for treatment-resistant depression. Verified SAINT Provider in Coral Springs, FL. Free consultation.",
            url: `${SITE_URL}/`,
            provider: CLINIC_REF,
            isPartOf: { "@id": SITE_ID },
            about: [
              "SAINT TMS",
              "Treatment-Resistant Depression",
              "TMS Therapy",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is SAINT® TMS?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared form of TMS for treatment-resistant depression. It uses fMRI to identify a stimulation target specific to your brain, and compresses treatment into five consecutive days instead of the 4–6 weeks required by standard TMS.",
                },
              },
              {
                "@type": "Question",
                name: "How is SAINT® different from standard TMS?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Two key differences: SAINT® uses fMRI to personalize the treatment target to your individual brain connectivity, while standard TMS uses anatomical landmarks. And SAINT® is delivered over five days, while standard TMS runs 5 days a week for 4–6 weeks.",
                },
              },
              {
                "@type": "Question",
                name: "Does insurance cover SAINT® TMS?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "There are two ways to access SAINT®. We accept Blue Cross Blue Shield in select states and the Blue Cross Blue Shield Federal Employee Program in select states — because insurance requires a review and approval process, this pathway generally takes longer from consultation to treatment. Our most common option is self-pay, which typically ranges from $25,000 to $28,000 and offers the greatest speed and flexibility, with most patients moving from consultation to treatment within a few days. Coverage varies by state and plan and is not guaranteed. Contact us to determine eligibility and the fastest path to care.",
                },
              },
              {
                "@type": "Question",
                name: "How quickly can I start treatment?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "With self-pay — our most common option, typically ranging from $25,000 to $28,000 — most patients move from consultation to treatment within a few days. We offer appointments seven days a week, extended care team hours, and private treatment suites. The insurance pathway generally takes longer because it requires benefits review and prior authorization. Our team guides you through every step regardless of which pathway you choose.",
                },
              },
              {
                "@type": "Question",
                name: "Do I need my own MRI first?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. We order and coordinate the resting-state fMRI for you. You do not need to arrange or bring your own scan.",
                },
              },
              {
                "@type": "Question",
                name: "Is SAINT® safe? Are there side effects?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "SAINT® is FDA-cleared and non-invasive. You stay awake and alert throughout — no anesthesia or sedation. The most common side effects are mild, temporary headache or scalp discomfort. Over 400 patients have been treated in clinical trials with no serious adverse events reported. Rare risks include seizure; discuss all risks with your provider.",
                },
              },
              {
                "@type": "Question",
                name: "Who is eligible for SAINT®?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "SAINT® is cleared for adults 22 and older with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication. You must be able to attend five consecutive full days and be able to have an fMRI. A free consultation determines whether you're a good candidate.",
                },
              },
              {
                "@type": "Question",
                name: "How does the consultation work? Is it in person or via Zoom?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The initial consultation is held conveniently over a Zoom web conference call with our clinical care team. As your steps evolve, you will also have the option to meet with our psychiatrist by Zoom web conference or in person at our Coral Springs clinic. The mandatory in-person steps include the fMRI brain scan and the five-day treatment series.",
                },
              },
            ],
          },
        ]}
      />

      <Navbar />
      <main>
        {/* Above-the-fold — loaded eagerly */}
        <Hero />

        {/* Featured patient journey film */}
        <section className="bg-background py-10 sm:py-14">
          <div className="container-px mx-auto max-w-4xl px-4">
            <div className="mb-6 text-center">
              <span className="text-sm font-semibold uppercase tracking-widest text-accent">
                Watch &amp; Learn
              </span>
              <h2 className="mt-2 font-headings text-2xl font-bold text-primary sm:text-3xl">
                The SAINT® Patient Journey
              </h2>
              <p className="mx-auto mt-2 max-w-xl text-base text-muted-foreground">
                See what the five-day accelerated treatment experience looks
                like, from brain mapping to the final session.
              </p>
            </div>
            <div className="relative w-full overflow-hidden rounded-2xl border border-border shadow-premium bg-black">
              <YouTubeFacade
                id="pZRgDnrmMvI"
                title="The SAINT TMS Patient Journey"
              />
            </div>
          </div>
        </section>

        {/* SAINT explainer videos — clustered together at top of page */}
        <SaintVideoSection variant="home" />
        <BrandSectionDivider flip />

        {/* Founder-focused SAINT video */}
        <SaintVideoSection variant="founder" />
        <BrandSectionDivider flip />

        {/* Below-the-fold — lazy chunks load after hydration; SSR streams
            content for crawlers. Each section in its own Suspense boundary
            for independent (progressive) hydration. */}
        <Suspense fallback={null}>
          <TmsTherapy />
        </Suspense>
        <BrandSectionDivider flip />
        <Suspense fallback={null}>
          <SaintStats />
        </Suspense>
        <Suspense fallback={null}>
          <HowItWorks />
        </Suspense>

        <BrandSectionDivider />
        <Suspense fallback={null}>
          <TwoWaysToGetStarted />
        </Suspense>
        <BrandSectionDivider flip />
        <Suspense fallback={null}>
          <NewsAndHappenings />
        </Suspense>
        <BrandSectionDivider />
        <Suspense fallback={null}>
          <SaintComparisonCallout />
        </Suspense>
        <BrandSectionDivider flip />
        <Suspense fallback={null}>
          <WhyUs />
        </Suspense>
        <BrandSectionDivider />
        <Suspense fallback={null}>
          <Testimonials />
        </Suspense>
        <Suspense fallback={null}>
          <BrighterTomorrowBanner />
        </Suspense>
        <BrandSectionDivider flip />
        <Suspense fallback={null}>
          <Faq />
        </Suspense>
        <BrandSectionDivider />
        <Suspense fallback={null}>
          <Consultation />
        </Suspense>
        <Suspense fallback={null}>
          <RelatedPages
            heading="Explore Our Treatments & Resources"
            links={[
              {
                title: "SAINT® Accelerated TMS",
                href: "/saint",
                description: "FDA-cleared, 5-day fMRI-guided TMS protocol",
              },
              {
                title: "Ampa One Day Protocol",
                href: "/ampa",
                description: "Single-day accelerated TMS treatment",
              },
              {
                title: "TMS for Depression",
                href: "/services/depression/",
                description:
                  "FDA-cleared TMS for treatment-resistant depression",
              },
              {
                title: "SPRAVATO® (Esketamine)",
                href: "/services/spravato",
                description: "FDA-approved nasal spray for TRD",
              },
              {
                title: "Cost & Insurance",
                href: "/cost-insurance",
                description: "Financing options and insurance verification",
              },
              {
                title: "Am I a Candidate?",
                href: "/am-i-a-candidate",
                description: "Find out if TMS is right for you",
              },
            ]}
          />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
